/**
 * Profile service — all /UserProfile API calls.
 */
import userProfileApi from '../userProfile/userProfileApi';
import {
  PROFILE_TABS,
  PROFILE_TABS_PUBLIC,
  STORAGE_KEYS,
} from '../../constants';
import { buildFilePart } from '../../platform/filePart';
import { storage } from '../../platform/storage';
import {
  UserProfileUpsertRequest,
  UserProfileUpdateRequest,
  ChartDataRequest,
  UpdateMeasurementRequest,
  UserRelationRequest,
  InviteRequest,
  GetUserConnectionsRequest,
  BuddySearchRequest,
  AccountSettingsUpdateRequest,
  LeaderBoardListRequest,
  ReportReason,
  REPORT_REASON_OPTIONS,
} from '../../types';



// ─── API Calls ────────────────────────────────────────────────────────────────

/** Returns the authenticated user's full profile. */
const getProfile = () =>
  userProfileApi.getProfile();

/** Updates the authenticated user's profile. */
const updateProfile = async (payload: UserProfileUpdateRequest) => {
  const userId = await storage.getItem<string | number>(STORAGE_KEYS.USER_ID);
  const profileFields = [
    'firstName', 'lastName', 'genderId', 'countryId', 'heightCm', 'heightScaleUnit', 'weightKg', 'weightScaleUnit', 
    'dateOfBirth', 'profilePicture', 'profilePictureBlob', 'startWeight', 'goalWeight', 'startWeightKg', 'goalWeightKg'
  ];
  const hasProfileFields = Object.keys(payload).some(key => profileFields.includes(key));
  const hasPrivacyFields = Object.keys(payload).some(key => key.endsWith('Privacy') || key === 'bio' || key === 'aboutMe');

  let profileResponse;
  const profileFormData = new FormData();
  if (hasProfileFields) {
    if (userId) profileFormData.append('UserId', String(userId));
    if (payload.firstName !== undefined) profileFormData.append('FirstName', payload.firstName);
    if (payload.lastName !== undefined) profileFormData.append('LastName', payload.lastName);
    if (payload.genderId !== undefined) profileFormData.append('GenderId', String(payload.genderId));
    if (payload.countryId !== undefined) profileFormData.append('CountryId', String(payload.countryId));
    if (payload.heightCm !== undefined) {
      const cmVal = Number(payload.heightCm).toFixed(2);
      profileFormData.append('HeightCm', cmVal);
      profileFormData.append('currentHeightCm', cmVal);
    }
    if (payload.heightScaleUnit !== undefined) profileFormData.append('HeightScaleUnit', String(payload.heightScaleUnit));
    if (payload.weightKg !== undefined) profileFormData.append('WeightKg', String(payload.weightKg));
    if (payload.weightScaleUnit !== undefined) profileFormData.append('WeightScaleUnit', String(payload.weightScaleUnit));
    if (payload.dateOfBirth !== undefined) profileFormData.append('DateOfBirth', payload.dateOfBirth);
    if (payload.startWeightKg !== undefined) {
      profileFormData.append('StartWeightKg', String(payload.startWeightKg));
    } else if (payload.startWeight !== undefined) {
      profileFormData.append('StartWeightKg', String(payload.startWeight));
    }
    if (payload.goalWeightKg !== undefined) {
      profileFormData.append('GoalWeightKg', String(payload.goalWeightKg));
    } else if (payload.goalWeight !== undefined) {
      profileFormData.append('GoalWeightKg', String(payload.goalWeight));
    }

    if (payload.profilePicture || payload.profilePictureBlob) {
      const uri = payload.profilePicture ?? '';
      const blobName =
        typeof File !== 'undefined' && payload.profilePictureBlob instanceof File
          ? payload.profilePictureBlob.name
          : '';
      let filename = blobName || uri.split('/').pop() || 'profile.jpg';
      filename = filename.split('?')[0].split('#')[0] || 'profile.jpg';
      if (!filename.includes('.')) filename = 'profile.jpg';
      const type =
        payload.profilePictureBlob?.type
        || (filename.toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg');
      profileFormData.append(
        'ProfilePicture',
        buildFilePart({ uri, name: filename, type, blob: payload.profilePictureBlob }),
      );
    }
  }

  if (hasPrivacyFields) {
    if (payload.aboutMe !== undefined) profileFormData.append('AboutMe', payload.aboutMe);
    if (payload.bio !== undefined) profileFormData.append('Bio', payload.bio);
    if (payload.profileViewPrivacy !== undefined) profileFormData.append('ProfileViewPrivacy', String(payload.profileViewPrivacy));
    if (payload.weightPrivacy !== undefined) profileFormData.append('WeightPrivacy', String(payload.weightPrivacy));
    if (payload.heightPrivacy !== undefined) profileFormData.append('HeightPrivacy', String(payload.heightPrivacy));
    if (payload.countryPrivacy !== undefined) profileFormData.append('CountryPrivacy', String(payload.countryPrivacy));
    if (payload.bioPrivacy !== undefined) profileFormData.append('BioPrivacy', String(payload.bioPrivacy));
    if (payload.aboutMePrivacy !== undefined) profileFormData.append('AboutMePrivacy', String(payload.aboutMePrivacy));
    if (payload.dateOfBirthPrivacy !== undefined) profileFormData.append('DateOfBirthPrivacy', String(payload.dateOfBirthPrivacy));
    if (payload.genderPrivacy !== undefined) profileFormData.append('GenderPrivacy', String(payload.genderPrivacy));
    if (payload.profilePicturePrivacy !== undefined) profileFormData.append('ProfilePicturePrivacy', String(payload.profilePicturePrivacy));
  }

  if (hasProfileFields || hasPrivacyFields) {
    profileResponse = await userProfileApi.updateProfilePrivacySettings(profileFormData);
  }

  return profileResponse;
};

/** Insert or update a profile record (typically used after registration). */
const upsertProfile = (payload: UserProfileUpsertRequest) =>
  userProfileApi.upsertProfile(payload);

/** Returns lightweight user info decoded from the bearer token claims. */
const getUserInfo = () =>
  userProfileApi.getUserInfo();

/** Fetches another user's profile details. */
const getOtherUserProfile = (profileUserGuid: string) =>
  userProfileApi.getOtherUserProfile(profileUserGuid);

/** Returns weight chart data for the given date range and period. */
const getChartData = (payload: ChartDataRequest) =>
  userProfileApi.getChartData(payload);

/** Logs a new weight measurement for the authenticated user. */
const updateMeasurement = (payload: UpdateMeasurementRequest) =>
  userProfileApi.updateMeasurement(payload);

/** Performs a social action (Follow, UnFollow, Block, UnBlock, Agent) against another user. */
const userRelation = (payload: UserRelationRequest) => {
  return userProfileApi.userRelation({
    relatedUserId: payload.relatedUserId,
    action: payload.action,
  });
};

export type BlockRelationResult = {
  success: boolean;
  relatedUserId: number | string;
  action: 'block' | 'unblock';
  message?: string;
};

/**
 * Shared Block / Unblock helper for UserProfile/UserRelation.
 * Always sends `{ relatedUserId, action: "block" | "unblock" }` exactly once.
 */
const setBlockRelation = async (
  relatedUserId: number | string | null | undefined,
  shouldBlock: boolean,
): Promise<BlockRelationResult> => {
  const action: 'block' | 'unblock' = shouldBlock ? 'block' : 'unblock';

  if (!relatedUserId || (typeof relatedUserId === 'number' && (!Number.isFinite(relatedUserId) || relatedUserId <= 0))) {
    return {
      success: false,
      relatedUserId: relatedUserId ?? 0,
      action,
      message: 'Invalid user ID',
    };
  }

  try {
    const response = await userRelation({
      relatedUserId,
      action,
    });
    const ok =
      response.data?.success !== false &&
      (response.status === 200 || response.status === 201);

    return {
      success: ok,
      relatedUserId,
      action,
      message: response.data?.message,
    };
  } catch (err: any) {
    return {
      success: false,
      relatedUserId,
      action,
      message:
        err?.response?.data?.message || err?.message || 'Failed to update block status',
    };
  }
};

/** Accepts or declines a buddy invite from another user. */
const updateInvite = (payload: InviteRequest) =>
  userProfileApi.updateInvite(payload);

/** Returns a paginated list of followers, following, or buddies. */
const getUserConnections = (payload: GetUserConnectionsRequest, signal?: AbortSignal) =>
  userProfileApi.getUserConnections(payload, signal);

/** Fetches the list of users blocked by the authenticated user. */
const getBlockedUsers = () =>
  userProfileApi.getBlockedUsers();

/** Searches for matching buddies using filter criteria. POST /api/v1/UserProfile/BuddySearch */
const buddySearch = (payload: BuddySearchRequest) =>
  userProfileApi.buddySearch(payload);

const getAccountSettings = () =>
  userProfileApi.getAccountSettings();

const updateAccountSettings = (payload: AccountSettingsUpdateRequest) =>
  userProfileApi.updateAccountSettings(payload);

const getLeaderBoardList = (payload: LeaderBoardListRequest) =>
  userProfileApi.getLeaderBoardList(payload);


// ─── Local Static Data (no backend endpoint) ──────────────────────────────────

/** Returns local profile tab labels (no backend endpoint). */
const getProfileTabs = async () => ({ data: [...PROFILE_TABS] });

/** Returns local buddy-profile tab labels (no backend endpoint). */
const getBuddyProfileTabs = async () => ({ data: [...PROFILE_TABS_PUBLIC] });

const getReportReasons = async (): Promise<ReportReason[]> => REPORT_REASON_OPTIONS;


// ─── Export ───────────────────────────────────────────────────────────────────

const profileService = {
  // API-backed
  getProfile,
  updateProfile,
  upsertProfile,
  getUserInfo,
  getOtherUserProfile,
  getChartData,
  updateMeasurement,
  userRelation,
  setBlockRelation,
  updateInvite,
  getUserConnections,
  getBlockedUsers,
  buddySearch,
  getAccountSettings,
  updateAccountSettings,
  getLeaderBoardList,
  getAwards: userProfileApi.getAwards,
  reportUser: userProfileApi.reportUser,
  getReportReasonOptions: () => REPORT_REASON_OPTIONS,
  getUserBuddyPreferences: userProfileApi.getUserBuddyPreferences,
  buddyMatching: userProfileApi.buddyMatching,
  // Local static
  getProfileTabs,
  getBuddyProfileTabs,
  getReportReasons,
};

export default profileService;
